import { useState } from "react";
import type { Project, WorkUnit, Entity } from "../types";
import StateChip from "../atoms/StateChip";
import "./PanelProjectsKanban.css";

// Unified 11-column grid: chev | # | title+WU | description | projOwner | wuOwner
//                          | TOKEN EST | TOKEN ACTUAL | TIME EST | TIME ACTUAL | state
// PR3 area D (BOARDROOM-2026-04-25-S45-COMMS-WRITE-PATH-BUILD-PROPER):
//   - Drop standalone Δ column. Δ is rendered as inline subtext within the
//     TOKEN ACTUAL cell when both est and actual are present (e.g. "90k −10k"
//     with subtle colour: green if under-est, red if over-est).
//   - Add TIME ACTUAL column (position 10). Shows "—" until backend lands the
//     dispatch+completion-timestamp computation (open-question in PR3 — see
//     PR description).
// Earlier S45 LAYOUT-FIXES context (kept for code-archaeology continuity):
//   LAYOUT-1 widened projOwner + wuOwner. LAYOUT-2 renamed est columns and
//   reordered TOKEN ACTUAL into position 7. LAYOUT-3 added the progress bar
//   on TOKEN ACTUAL for project rows.
export const GRID =
  "14px 28px minmax(200px,0.9fr) minmax(280px,2.4fr) 130px 160px 80px 130px 70px 70px 96px";

type LaneId = "in-progress" | "ready" | "backlog" | "attention";

const LANES: { id: LaneId; label: string; blurb: string }[] = [
  { id: "in-progress", label: "In Progress", blurb: "actively being built (incl. blocked)" },
  { id: "ready", label: "Ready", blurb: "spec'd, waiting for capacity" },
  { id: "backlog", label: "Backlog", blurb: "captured — need scoping" },
  { id: "attention", label: "Needs attention", blurb: "done / cancelled / unrecognised state — never silently dropped" },
];

// BOARDROOM-2026-08-08-124017 step 3 ("kanban lane-vocab truth"): canonicalise
// the writer-declared state into one of the 4 dashboard tabs above. This is a
// PRESENTATIONAL grouping only — the raw p.state value is still rendered
// verbatim on every card via StateChip (see stateLabel below); nothing here
// mutates the underlying value, so it does not violate
// PROGRAMME-AD-SCHEMA-VOCABULARY-PER-ROW-TYPE LOCKED ("dashboard MUST render
// writer-declared states without translation").
//
// Before this fix the kanban filtered strictly on `p.lane === "in-progress" |
// "ready" | "backlog"` — an EXACT match against only 3 of the ~13 valid
// project-vocab values (PROGRAMME-AD-SCHEMA-VOCABULARY-PER-ROW-TYPE v=3: 7
// Title Case + 6 legacy). The backend's `lane` field is a pure pass-through
// of the writer-declared state (required by the same LOCKED AD, to fix the
// earlier S67 silent-drop bug) — it is NOT pre-bucketed into these 3 ids. So
// any project written as "In Progress" (Title Case), "blocked"/"Blocked",
// "done"/"Done", "Cancelled", "obsolete", or "In Review" matched NONE of the
// 3 tabs and was invisible on every lane, even though the API faithfully
// returned it — a UI-layer silent drop reintroducing exactly the class of
// bug the AD was locked to prevent, just one layer up. canonicalLane never
// returns a bucket outside LANES, so every project always lands somewhere.
function canonicalLane(p: Pick<Project, "state" | "lane" | "schema_violation">): LaneId {
  if (p.schema_violation) return "attention";
  const s = String(p.lane || p.state || "").trim().toLowerCase();
  if (s === "in progress" || s === "in-progress" || s === "blocked") return "in-progress";
  if (s === "ready") return "ready";
  if (s === "backlog") return "backlog";
  // done / cancelled / obsolete / in review / empty / anything else unknown:
  // never silently dropped — surfaced under "Needs attention" instead.
  return "attention";
}

function fmtK(n: number): string {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return Math.round(n / 1_000) + "k";
  return String(n);
}
function fmtDelta(d: number): string {
  if (d === 0) return "0";
  const s = d > 0 ? "+" : "−";
  return s + fmtK(Math.abs(d));
}
function fmtEta(hrs: number): string {
  if (!hrs) return "—";
  if (hrs < 8) return `${hrs}h`;
  const d = hrs / 8;
  if (d < 10) return `${d % 1 === 0 ? d : d.toFixed(1)}d`;
  return `${Math.round(d)}d`;
}

function GridHeader() {
  return (
    <div className="grid-header" style={{ gridTemplateColumns: GRID }}>
      <span aria-hidden="true" />
      <span className="gh-lbl gh-num">#</span>
      <span className="gh-lbl">Project / work unit</span>
      <span className="gh-lbl">Description</span>
      <span className="gh-lbl">Project owner</span>
      <span className="gh-lbl">WU owner</span>
      <span className="gh-lbl gh-r">Token est</span>
      <span className="gh-lbl gh-r">Token actual</span>
      <span className="gh-lbl gh-r">Time est</span>
      <span className="gh-lbl gh-r">Time actual</span>
      <span className="gh-lbl">State</span>
    </div>
  );
}

function TokenCell({
  actual,
  est,
  show,
  withBar = true,
}: {
  actual: number;
  est: number;
  show: boolean;
  withBar?: boolean;
}) {
  if (!show || actual === 0)
    return <span className="cell-blank">—</span>;
  const over = actual > est;
  // PR3 area D: inline delta subtext within the actual cell when est known.
  // Subtle colour: green if under-est, red if over-est, neutral grey if zero.
  const d = est ? actual - est : 0;
  const showDelta = est > 0;
  const deltaCls = d > 0 ? "is-over" : d < 0 ? "is-under" : "";
  if (!withBar) {
    return (
      <span className={`tok-cell tok-cell-nobar ${over ? "is-over" : ""}`}>
        <span className="tok-n">{fmtK(actual)}</span>
        {showDelta && d !== 0 && (
          <span className={`tok-delta ${deltaCls}`} aria-label={`delta ${fmtDelta(d)}`}>
            {fmtDelta(d)}
          </span>
        )}
      </span>
    );
  }
  const pct = est ? Math.min(100, Math.round((actual / est) * 100)) : 0;
  return (
    <span className={`tok-cell ${over ? "is-over" : ""}`}>
      <span className="tok-n">{fmtK(actual)}</span>
      {showDelta && d !== 0 && (
        <span className={`tok-delta ${deltaCls}`} aria-label={`delta ${fmtDelta(d)}`}>
          {fmtDelta(d)}
        </span>
      )}
      <span className="tok-bar" aria-hidden="true">
        <span className="tok-fill" style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}

// PR3 area D: TIME ACTUAL column placeholder. Backend computation from
// dispatch+completion timestamps is open-question in PR3 (see PR
// description). Renders "—" with explanatory tooltip until backend lands.
function TimeActualCell() {
  return (
    <span
      className="cell-blank cell-r"
      title="Time actual: backend computation from dispatch + completion timestamps not yet implemented; cell shows — until then"
    >
      —
    </span>
  );
}

function TitleInline({
  name,
  wuCurrent,
  wuTotal,
}: {
  name: string;
  wuCurrent?: number;
  wuTotal?: number;
}) {
  return (
    <span className="cell-title-inline">
      <span className="cell-title-main">{name}</span>
      {wuTotal ? (
        <span className="cell-title-wux">
          {wuCurrent ?? 0}/{wuTotal}
        </span>
      ) : null}
    </span>
  );
}

// ─── In-Progress lane row ────────────────────────────────────────
function ProjectRowIP({
  p,
  expanded,
  onToggle,
  onBlockedClick,
}: {
  p: Project;
  expanded: boolean;
  onToggle: () => void;
  onBlockedClick: (payload: Project | { escalationRef: string }) => void;
}) {
  const blockedClickable =
    String(p.state).trim().toLowerCase() === "blocked";
  // Per PROGRAMME-AD-SCHEMA-VOCABULARY-PER-ROW-TYPE LOCKED: render the
  // writer-declared state as-is (no translation). The prior ternary here
  // defaulted every unrecognised state (e.g. "Done", "Cancelled", "In
  // Review") to the label "Backlog" — a second, more subtle instance of the
  // lane-vocab-chaos bug (wrong label, not just wrong tab).
  const stateLabel = p.state || "unknown";

  const handleChipMouse = (e: React.MouseEvent) => {
    if (!blockedClickable) return;
    e.stopPropagation();
    onBlockedClick(p);
  };

  return (
    <div className={`prow prow-${p.state} ${expanded ? "is-open" : ""}`}>
      <button
        type="button"
        className="row-grid prow-head"
        style={{ gridTemplateColumns: GRID }}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`prow-detail-${p.id}`}
      >
        <span className="cell-chev" aria-hidden="true">
          {expanded ? "▾" : "▸"}
        </span>
        <span className="cell-num">P{p.priority}</span>
        <TitleInline name={p.name} wuCurrent={p.wuCurrent} wuTotal={p.wuTotal} />
        <span className="cell-desc" title={p.desc}>
          {p.desc}
        </span>
        <span className="cell-owner">{p.owner}</span>
        <span className="cell-blank">—</span>
        <span className="cell-r cell-est">{fmtK(p.tokensEst)}</span>
        <TokenCell actual={p.tokensActual} est={p.tokensEst} show />
        <span className="cell-r cell-eta">{fmtEta(p.etaHrs)}</span>
        <TimeActualCell />
        {/* State chip inside the expand button must not be a focusable descendant (WCAG nested-interactive).
            Mouse-click still jumps via onClick; keyboard users reach the jump action through the
            "Jump to blocker" button in the expanded detail region. */}
        <span
          className={`cell-state ${blockedClickable ? "cell-state-link" : ""}`}
          onClick={handleChipMouse}
          title={
            blockedClickable ? "click: jump to blocking decision / WU" : undefined
          }
        >
          <StateChip state={stateLabel} />
        </span>
      </button>
      {expanded && p.wus.length > 0 && (
        <div className="prow-detail" id={`prow-detail-${p.id}`}>
          {blockedClickable && (
            <button
              type="button"
              className="prow-jump-btn"
              onClick={() => onBlockedClick(p)}
            >
              Jump to blocker ({p.escalationRef ?? "next Blocked WU"})
            </button>
          )}
          {p.wus.map((w) => (
            <WuRow
              key={w.n}
              project={p}
              wu={w}
              onBlockedClick={onBlockedClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function WuRow({
  project,
  wu,
  onBlockedClick,
}: {
  project: Project;
  wu: WorkUnit;
  onBlockedClick: (payload: Project | { escalationRef: string }) => void;
}) {
  const xfer = wu.entity !== project.owner;
  const wuBlocked = wu.state === "Blocked" && !!wu.escalationRef;

  const handleChipMouse = (e: React.MouseEvent) => {
    if (!wuBlocked || !wu.escalationRef) return;
    e.stopPropagation();
    onBlockedClick({ escalationRef: wu.escalationRef });
  };
  const handleJumpBtn = () => {
    if (!wu.escalationRef) return;
    onBlockedClick({ escalationRef: wu.escalationRef });
  };

  return (
    <div
      className={`wu-row row-grid wu-${wu.state.toLowerCase().replace(/\s+/g, "-")}`}
      style={{ gridTemplateColumns: GRID }}
      data-wu-ref={`${project.id}-${wu.n}`}
    >
      <span className="cell-chev" />
      <span className="cell-num cell-num-wu">{wu.n}</span>
      <span className="cell-title-inline cell-title-wu">
        <span className="cell-title-main">{wu.title}</span>
      </span>
      {wu.desc ? (
        <span className="cell-desc cell-desc-wu" title={wu.desc}>
          {wu.desc}
        </span>
      ) : (
        <span className="cell-blank cell-desc-inherit" aria-label="inherits project description">
          ↳
        </span>
      )}
      <span
        className="cell-blank cell-owner-inherit"
        aria-label="inherits project owner"
      >
        ↳
      </span>
      <span className={`cell-owner cell-owner-wu ${xfer ? "is-xfer" : ""}`}>
        {wu.entity}
        {xfer && (
          <span className="xfer-tag" title={`dispatched from ${project.owner}`}>
            dispatched
          </span>
        )}
      </span>
      <span className="cell-r cell-est">{fmtK(wu.est)}</span>
      <TokenCell actual={wu.actual} est={wu.est} show withBar={false} />
      <span className="cell-r cell-eta">{fmtEta(wu.etaHrs)}</span>
      <TimeActualCell />
      {wuBlocked ? (
        <button
          type="button"
          className="cell-state cell-state-link cell-state-btn"
          onClick={handleChipMouse}
          onKeyUp={(e) => {
            if (e.key === "Enter" || e.key === " ") handleJumpBtn();
          }}
          title={`click: jump to ${wu.escalationRef}`}
          aria-label={`Jump to blocker ${wu.escalationRef}`}
        >
          <StateChip state={wu.state} />
        </button>
      ) : (
        <span className="cell-state">
          <StateChip state={wu.state} />
        </span>
      )}
    </div>
  );
}

// ─── Ready lane row — Actual/Δ blank ─────────────────────────────
function ProjectRowReady({
  p,
  expanded,
  onToggle,
}: {
  p: Project;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`prow prow-ready ${expanded ? "is-open" : ""}`}>
      <button
        type="button"
        className="row-grid prow-head"
        style={{ gridTemplateColumns: GRID }}
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`prow-detail-${p.id}`}
      >
        <span className="cell-chev" aria-hidden="true">
          {expanded ? "▾" : "▸"}
        </span>
        <span className="cell-num">P{p.priority}</span>
        <TitleInline name={p.name} wuCurrent={0} wuTotal={p.wuTotal} />
        <span className="cell-desc" title={p.desc}>
          {p.desc}
        </span>
        <span className="cell-owner">{p.owner}</span>
        <span className="cell-blank">—</span>
        <span className="cell-r cell-est">{fmtK(p.tokensEst)}</span>
        <span className="cell-blank cell-r">—</span>
        <span className="cell-r cell-eta">{fmtEta(p.etaHrs)}</span>
        <span className="cell-blank cell-r">—</span>
        <span className="cell-state">
          <StateChip state="Ready" />
        </span>
      </button>
      {expanded && p.wus.length > 0 && (
        <div className="prow-detail" id={`prow-detail-${p.id}`} role="region">
          {p.wus.map((w) => {
            const xfer = w.entity !== p.owner;
            return (
              <div
                key={w.n}
                className="wu-row row-grid wu-ready"
                style={{ gridTemplateColumns: GRID }}
              >
                <span className="cell-chev" />
                <span className="cell-num cell-num-wu">{w.n}</span>
                <span className="cell-title-inline cell-title-wu">
                  <span className="cell-title-main">{w.title}</span>
                </span>
                {w.desc ? (
                  <span className="cell-desc cell-desc-wu" title={w.desc}>
                    {w.desc}
                  </span>
                ) : (
                  <span className="cell-blank cell-desc-inherit">↳</span>
                )}
                <span className="cell-blank cell-owner-inherit">↳</span>
                <span
                  className={`cell-owner cell-owner-wu ${xfer ? "is-xfer" : ""}`}
                >
                  {w.entity}
                  {xfer && <span className="xfer-tag">dispatched</span>}
                </span>
                <span className="cell-r cell-est">{fmtK(w.est)}</span>
                <span className="cell-blank cell-r">—</span>
                <span className="cell-r cell-eta">{fmtEta(w.etaHrs)}</span>
                <span className="cell-blank cell-r">—</span>
                <span className="cell-state">
                  <StateChip state="Ready" />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Backlog lane — stub row ─────────────────────────────────────
function ProjectRowBacklog({ p }: { p: Project }) {
  return (
    <div className="prow prow-backlog">
      <div
        className="row-grid prow-head prow-head-static"
        style={{ gridTemplateColumns: GRID }}
      >
        <span className="cell-chev" />
        <span className="cell-num">P{p.priority}</span>
        <TitleInline name={p.name} />
        <span className="cell-desc" title={p.desc}>
          {p.desc}
        </span>
        <span className="cell-owner">{p.owner}</span>
        <span className="cell-blank">—</span>
        <span className="cell-blank cell-r">—</span>
        <span className="cell-blank cell-r">—</span>
        <span className="cell-blank cell-r">—</span>
        <span className="cell-blank cell-r">—</span>
        <span className="cell-state">
          <StateChip state="needs-definition" />
        </span>
      </div>
    </div>
  );
}

// ─── Kanban shell ────────────────────────────────────────────────
export default function PanelProjectsKanban({
  projects,
  entities: _entities,
}: {
  projects: Project[];
  entities: Entity[];
}) {
  const [lane, setLane] = useState<LaneId>("in-progress");
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());
  const toggleOpen = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const [blockedCursor, setBlockedCursor] = useState<Record<string, number>>(
    {},
  );
  // BOARDROOM-2026-08-08-124017 step 3 ("hide projects untouched >21 days"):
  // stale=true projects (computed server-side, see handler.py _PROJECT_STALE_DAYS)
  // default-collapse into a per-lane toggle instead of being dropped — the
  // LOCKED AD forbids the API from ever dropping rows, so the collapse has
  // to happen here, and only as a reveal-able UI state, never a data loss.
  const [staleOpen, setStaleOpen] = useState(false);

  const counts: Record<LaneId, number> = {
    "in-progress": projects.filter((p) => canonicalLane(p) === "in-progress").length,
    ready: projects.filter((p) => canonicalLane(p) === "ready").length,
    backlog: projects.filter((p) => canonicalLane(p) === "backlog").length,
    attention: projects.filter((p) => canonicalLane(p) === "attention").length,
  };
  const laneProjects = projects
    .filter((p) => canonicalLane(p) === lane)
    .sort((a, b) => a.priority - b.priority);
  const visible = laneProjects.filter((p) => !p.stale);
  const staleVisible = laneProjects.filter((p) => p.stale);
  const activeLane = LANES.find((l) => l.id === lane)!;

  const jumpToDecision = (
    escId: string,
    { pulse = true }: { pulse?: boolean } = {},
  ): boolean => {
    const target = document.querySelector(`[data-decision-id="${escId}"]`);
    if (!target) return false;
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    if (pulse) {
      target.classList.add("drow-pulse");
      window.setTimeout(
        () => target.classList.remove("drow-pulse"),
        1800,
      );
    }
    return true;
  };

  const handleBlockedClick = (
    payload: Project | { escalationRef: string },
  ) => {
    // WU-row chip path: payload only has escalationRef, not wus.
    if ("escalationRef" in payload && !("wus" in payload)) {
      if (payload.escalationRef) jumpToDecision(payload.escalationRef);
      return;
    }
    const p = payload as Project;
    const blockedWus = (p.wus || []).filter((w) => w.state === "Blocked");
    if (blockedWus.length === 0) {
      if (p.escalationRef) jumpToDecision(p.escalationRef);
      return;
    }
    const cursor = blockedCursor[p.id] || 0;
    const wu = blockedWus[cursor % blockedWus.length];
    setBlockedCursor((prev) => ({
      ...prev,
      [p.id]: (cursor + 1) % blockedWus.length,
    }));
    setOpenIds((prev) => new Set(prev).add(p.id));
    if (wu.escalationRef) jumpToDecision(wu.escalationRef);
    window.setTimeout(() => {
      const wuEl = document.querySelector(
        `[data-wu-ref="${p.id}-${wu.n}"]`,
      );
      if (wuEl) {
        wuEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
        wuEl.classList.add("wu-row-pulse");
        window.setTimeout(
          () => wuEl.classList.remove("wu-row-pulse"),
          1800,
        );
      }
    }, 150);
  };

  return (
    <section
      className={`panel panel-b panel-kanban ${projects.length === 0 ? "is-empty" : "is-populated"}`}
      aria-labelledby="panel-b-title"
    >
      <header className="panel-head kanban-head">
        <div className="kanban-head-left">
          <h2 className="panel-title" id="panel-b-title">
            Projects
          </h2>
          <nav className="kanban-tabs" role="tablist" aria-label="Project lanes">
            {LANES.map((l) => (
              <button
                key={l.id}
                type="button"
                role="tab"
                id={`tab-${l.id}`}
                aria-selected={lane === l.id}
                aria-controls={`tabpanel-${l.id}`}
                tabIndex={lane === l.id ? 0 : -1}
                className={`kanban-tab ${lane === l.id ? "is-active" : ""}`}
                onClick={() => {
                  setLane(l.id);
                  setOpenIds(new Set());
                  setStaleOpen(false);
                }}
              >
                <span className="kanban-tab-label">{l.label}</span>
                <span className="kanban-tab-count">{counts[l.id]}</span>
              </button>
            ))}
          </nav>
        </div>
        <span className="panel-sub kanban-blurb">{activeLane.blurb}</span>
      </header>
      <GridHeader />
      <div
        className="panel-body panel-body-grid"
        role="tabpanel"
        id={`tabpanel-${lane}`}
        aria-labelledby={`tab-${lane}`}
      >
        {visible.length === 0 && staleVisible.length === 0 && (
          <div className="panel-empty">
            No projects in {activeLane.label.toLowerCase()}.
          </div>
        )}
        {(lane === "in-progress" || lane === "attention") &&
          visible.map((p) => (
            <ProjectRowIP
              key={p.id}
              p={p}
              expanded={openIds.has(p.id)}
              onToggle={() => toggleOpen(p.id)}
              onBlockedClick={handleBlockedClick}
            />
          ))}
        {lane === "ready" &&
          visible.map((p) => (
            <ProjectRowReady
              key={p.id}
              p={p}
              expanded={openIds.has(p.id)}
              onToggle={() => toggleOpen(p.id)}
            />
          ))}
        {lane === "backlog" &&
          visible.map((p) => <ProjectRowBacklog key={p.id} p={p} />)}

        {staleVisible.length > 0 && (
          <div className="panel-subsection">
            <button
              type="button"
              className="panel-subsection-toggle"
              onClick={() => setStaleOpen((v) => !v)}
              aria-expanded={staleOpen}
            >
              {staleOpen ? "▾" : "▸"} Stale (untouched 21d+){" "}
              <span className="panel-count">{staleVisible.length}</span>
            </button>
            {staleOpen &&
              (lane === "ready" ? (
                staleVisible.map((p) => (
                  <ProjectRowReady
                    key={p.id}
                    p={p}
                    expanded={openIds.has(p.id)}
                    onToggle={() => toggleOpen(p.id)}
                  />
                ))
              ) : lane === "backlog" ? (
                staleVisible.map((p) => <ProjectRowBacklog key={p.id} p={p} />)
              ) : (
                staleVisible.map((p) => (
                  <ProjectRowIP
                    key={p.id}
                    p={p}
                    expanded={openIds.has(p.id)}
                    onToggle={() => toggleOpen(p.id)}
                    onBlockedClick={handleBlockedClick}
                  />
                ))
              ))}
          </div>
        )}
      </div>
    </section>
  );
}
